import { RequestScope } from 'api/axios'
import { DataService } from 'api/DataService'
import { AxiosRequestConfig } from 'axios'
import { GetServerSidePropsContext } from 'next'

type Response<T> = {
  detail_error: string
  error: boolean
  message: string
  data: T
  status: number
}

type UrlParams = {
  rowsPerPage?: number
  page?: number
  uuid?: string
  filter_by?: string
}

type RoutesParams = {
  options?: AxiosRequestConfig
  ctx?: GetServerSidePropsContext
} & UrlParams &
  RequestScope

interface Routes {
  name: RequestName
  type: 'POST' | 'GET' | 'DELETE' | 'PATCH'
  url: (params?: UrlParams) => string
}

type RequestName =
  | 'customerCreate'
  | 'customerList'
  | 'receivables'
  | 'customerArchive'
  | 'statisticsGet'
  | 'customerGroupsList'
  | 'customerChargesList'
  | 'getTransfers'
  | 'customerRecurrencesList'
  | 'getCreditCards'
  | 'getCreditCard'
  | 'deleteCreditCard'
  | 'getContracts'
  | 'getAnnouncementPortals'
  | 'getInvoices'
  | 'nfseCities'
  | 'createNfse'
  | 'getNfse'
  | 'editNfse'
  | 'deleteNfse'
  | 'getProviders'
  | 'getFieldsLinkRegistration'
  | 'getCustomFields'
  | 'balanceDetails'
  | 'getCustomerDocuments'
  | 'customerRecentChargesList'
  | 'getAccountsPayablesList'
  | 'getCustomFieldsWithoutPagination'
  | 'customerInstallmentsList'
  | 'getInstallmentDetail'
  | 'getAccountsReceivableList'
  | 'getFinancialCashFlow'
  | 'workflowEntriesList'
  | 'workflowEntryDetail'
  | 'getExpenseDetails'
  | 'antecipateBillingsBatch'
  | 'tagBillingsBatch'
  | 'notifyBillingsBatch'
  | 'payBillingsBatch'
  | 'nfseConfigBillingsBatch'
  | 'nfseConfigRecurrencesBatch'

type Requests = {
  [key in RequestName]: <T = any>(params?: RoutesParams) => Promise<Response<T>>
}

const routes: Routes[] = [
  {
    name: 'customerCreate',
    type: 'GET',
    url: () => '/v1/customers'
  },
  {
    name: 'customerList',
    type: 'GET',
    url: (params?: UrlParams) => `/v1/customers/${params?.uuid}`
  },
  {
    name: 'receivables',
    type: 'GET',
    url: (params?: UrlParams) =>
      `/v1/receivables?per=${params?.rowsPerPage || 25}&page=${
        (params?.page || 0) + 1
      }`
  },
  {
    name: 'customerArchive',
    type: 'POST',
    url: (params?: UrlParams) => `/v1/customers/${params?.uuid}`
  },
  {
    name: 'statisticsGet',
    type: 'GET',
    url: () => '/v1/statistics'
  },
  {
    name: 'customerChargesList',
    type: 'GET',
    url: (params?: UrlParams) => `/v3/billings${params?.filter_by}`
  },
  {
    name: 'customerInstallmentsList',
    type: 'GET',
    url: (params?: UrlParams) => `/v3/billings/installments${params?.filter_by}`
  },
  {
    name: 'getInstallmentDetail',
    type: 'GET',
    url: (params?: UrlParams) => `/v3/billings/installments/${params?.uuid}`
  },
  {
    name: 'customerRecurrencesList',
    type: 'GET',
    url: (params?: UrlParams) => `/v3/recurrences${params?.filter_by}`
  },
  {
    name: 'customerGroupsList',
    type: 'GET',
    url: (params?: UrlParams) =>
      `/v1/customer_groups?per=${params?.rowsPerPage || 25}&page=${
        (params?.page || 0) + 1
      }`
  },
  {
    name: 'getCreditCards',
    type: 'GET',
    url: () => `/v1/credit_cards`
  },
  {
    name: 'getCreditCard',
    type: 'GET',
    url: (params) => `/v1/customer/${params?.uuid}/credit_cards`
  },
  {
    name: 'deleteCreditCard',
    type: 'DELETE',
    url: (params) => `/v1/credit_cards/${params?.uuid}`
  },
  {
    name: 'getContracts',
    type: 'GET',
    url: (params?: UrlParams) =>
      `/v1/contracts${params?.uuid ? `/${params?.uuid}` : ''}${
        params?.filter_by
      }`
  },
  {
    name: 'getTransfers',
    type: 'GET',
    url: (params) => `/v1/transfers${params?.filter_by}`
  },
  {
    name: 'getAnnouncementPortals',
    type: 'GET',
    url: (params?: UrlParams) => `/v1/billings${params?.filter_by}`
  },
  {
    name: 'getInvoices',
    type: 'GET',
    url: (params?: UrlParams) => `/v1/nfses${params?.filter_by}`
  },
  {
    name: 'getProviders',
    type: 'GET',
    url: (params?: UrlParams) => `/v1/nfse_issuers${params?.filter_by || ''}`
  },
  {
    name: 'nfseCities',
    type: 'GET',
    url: () => `/v1/nfse_cities`
  },
  {
    name: 'createNfse',
    type: 'POST',
    url: () => `/v1/nfse_issuers`
  },
  {
    name: 'editNfse',
    type: 'PATCH',
    url: (params) => `/v1/nfse_issuers/${params?.uuid}`
  },
  {
    name: 'getNfse',
    type: 'GET',
    url: (params) => `/v1/nfse_issuers/${params?.uuid}`
  },
  {
    name: 'deleteNfse',
    type: 'DELETE',
    url: (params) => `/v1/nfse_issuers/${params?.uuid}`
  },
  {
    name: 'getFieldsLinkRegistration',
    type: 'GET',
    url: () => `/v1/extra_fields`
  },
  {
    name: 'getCustomFields',
    type: 'GET',
    url: () => `v1/custom_fields`
  },
  {
    name: 'getCustomFieldsWithoutPagination',
    type: 'GET',
    url: () => `v1/custom_fields/all`
  },
  {
    name: 'balanceDetails',
    type: 'GET',
    url: () => 'v2/merchants/balance_details'
  },
  {
    name: 'getAccountsPayablesList',
    type: 'GET',
    url: (params?: UrlParams) => `/v1/accounts_payables${params?.filter_by}`
  },
  {
    name: 'getCustomerDocuments',
    type: 'GET',
    url: (params) =>
      `/v1/customers/${params?.uuid}/documents${params?.filter_by}`
  },
  {
    name: 'customerRecentChargesList',
    type: 'GET',
    url: (params) => `/v1/billings/customer/${params?.uuid}/recents`
  },
  {
    name: 'getAccountsReceivableList',
    type: 'GET',
    url: (params?: UrlParams) => `/v1/accounts_receivable${params?.filter_by}`
  },
  {
    name: 'getFinancialCashFlow',
    type: 'GET',
    url: (params?: UrlParams) =>
      `/v1/financial/cash_flow${params?.filter_by || ''}`
  },
  {
    name: 'workflowEntriesList',
    type: 'GET',
    url: (params?: UrlParams) =>
      `/v3/workflow_entries${params?.filter_by || ''}`
  },
  {
    name: 'workflowEntryDetail',
    type: 'GET',
    url: (params?: UrlParams) => `/v3/workflow_entries/${params?.uuid}`
  },
  {
    name: 'getExpenseDetails',
    type: 'GET',
    url: (params?: UrlParams) => `/v2/expenses/${params?.uuid}`
  },
  {
    name: 'antecipateBillingsBatch',
    type: 'POST',
    url: () => '/v3/billings/antecipate_batch'
  },
  {
    name: 'tagBillingsBatch',
    type: 'POST',
    url: () => '/v3/billings/tag_batch'
  },
  {
    name: 'notifyBillingsBatch',
    type: 'POST',
    url: () => '/v3/billings/notify_batch'
  },
  {
    name: 'payBillingsBatch',
    type: 'POST',
    url: () => '/v3/billings/pay_batch'
  },
  {
    name: 'nfseConfigBillingsBatch',
    type: 'POST',
    url: () => '/v3/billings/nfse_config_batch'
  },
  {
    name: 'nfseConfigRecurrencesBatch',
    type: 'POST',
    url: () => '/v3/recurrences/nfse_config_batch'
  }
]

const requests = {} as Requests

routes.map(
  ({ name, type, url }) =>
    (requests[name] = async <T = any>(params?: RoutesParams) =>
      DataService<T>({
        url: url(params),
        data: params?.options,
        type,
        ctx: params?.ctx,
        scope: params?.scope,
        merchantUuid: params?.merchantUuid
      }))
)

export const {
  customerCreate,
  customerList,
  receivables,
  customerArchive,
  statisticsGet,
  customerGroupsList,
  customerChargesList,
  customerRecurrencesList,
  getCreditCards,
  getCreditCard,
  deleteCreditCard,
  getContracts,
  getTransfers,
  getInvoices,
  nfseCities,
  createNfse,
  getNfse,
  editNfse,
  deleteNfse,
  getProviders,
  getFieldsLinkRegistration,
  getCustomFields,
  balanceDetails,
  getCustomerDocuments,
  getAccountsPayablesList,
  customerRecentChargesList,
  customerInstallmentsList,
  getInstallmentDetail,
  getCustomFieldsWithoutPagination,
  getAccountsReceivableList,
  workflowEntriesList,
  workflowEntryDetail,
  getExpenseDetails,
  antecipateBillingsBatch,
  tagBillingsBatch,
  notifyBillingsBatch,
  payBillingsBatch,
  nfseConfigBillingsBatch,
  nfseConfigRecurrencesBatch
} = requests
